import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Box,
  IconButton,
  TextField,
  Menu,
  MenuItem,
  Divider,
  InputAdornment, Skeleton
} from "@mui/material";
import { useOutletContext, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { esignToolApi } from "../../services/esignApi";
import { pdfToolApi } from "../../services/PdfEditor/pdfToolApi";
import { esignIcons } from "../Esign/icons";
import { getStatusStyles } from "../../heplers";
import { uploadDocument } from "../../store/reducers/uploadDocumentSlice";
import { apiEndpoint } from "../../urls";
import { getEsignDocumentUrl, getPdfEditorDocumentUrl, getSignedFinalPdfUrl } from "../../utils/pdfUrl";
import Loader from "../Loader";
import { refreshToken } from "../../store/reducers/pdfEditorTokenSlice";
import PdfPreview from "../PdfPreview/pdfPreview";
import { debounce } from "lodash";
import DocumentDetails from "./documnetDetails";
import { useLogger } from "../../hooks/useLogger";

const EsignedDocument = () => {
  const logEvent = useLogger();
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openDetails, setOpenDetails] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);
  const { open } = useOutletContext();
  const { profileData } = useSelector((state) => state.profile);
  const { pdfEditortoken } = useSelector((state) => state.pdfEditortoken);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loader, setLoader] = useState(false);
  const [tableloader, setTableLoader] = useState(false);
  const [previewDocument, setPreviewDocument] = useState(false);
  const [previewDoc, setPreviewDoc] = useState({});

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [selectedPreviewDoc, setSelectedPreviewDoc] = useState(null);
  const [signersStatus, setSignersStatus] = useState([]);
  const [selectedDetailDoc, setSelectedDetailDoc] = useState(null);

  useEffect(() => {
      fetchAllDocuments(currentPage, rowsPerPage, searchText);
  }, [currentPage, searchText, rowsPerPage]);

  const fetchAllDocuments = async (page, rowsPerPage, searchText) => {
    setTableLoader(true)
    try {
      const response = await esignToolApi.getAllEsignedDocument(page, rowsPerPage, searchText);
      setTableLoader(false)
      if (response) {
        // console.log(response)
        setDocuments(response?.data);
        setTotalPages(response?.pages);        
        setTotalEntries(response?.total_records);
      }
    } catch (error) {
      setTableLoader(false)
      console.error("Error fetching data:", error);
    }
  };

  const handleChangePage = (_, newPage) => {
    console.log('click', newPage, totalPages)
    if (newPage + 1 <= totalPages) {
      setCurrentPage(newPage + 1);
      fetchAllDocuments(newPage + 1, rowsPerPage, searchText);
    }
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(1); // Reset to first page when changing rows per page
  };

  const handleMenuOpen = (event, doc) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedDoc(doc);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedDoc(null);
  };

  const handleExpire = () => {
    console.log("Expire document:", selectedDoc?.document_id);
    handleMenuClose();
  };

  useEffect(() => {
    if (selectedDoc) {
      handleNewRequest(selectedDoc);
    }
  }, [selectedDoc]);

  const handleNewRequest = async (selectedDoc) => {
    logEvent('Clicked', 'New Request Clicked', 'Documents/Esigned');
    if(pdfEditortoken?.access && selectedDoc?.document_id)
    {
      setLoader(true)
      console.log(selectedDoc)
      try {
        const response = await pdfToolApi.getSelectedDocuments(pdfEditortoken?.access, selectedDoc?.document_id, selectedDoc?.request_id );
        setLoader(false)
        if (response.status) {
          console.log(response)
          const documentUrl = getPdfEditorDocumentUrl(response?.data);

          if (!documentUrl) {
              console.error("Document URL not found in response.");
              return;
          }
          const checkResponse = await fetch(documentUrl, { method: 'GET' });
          if (!checkResponse.ok) {
              console.error(`Document not found: ${documentUrl}`);
              return;
          }
          uploadFileFromUrl(documentUrl)
        }
      } catch (error) {
        setLoader(false)
        if (error.response?.status === 401 && error.response?.data?.code === "token_not_valid") {
          console.warn("Access token expired, attempting to refresh...");
          dispatch(refreshToken())
            .unwrap()
            .then((newTokenData) => {
              if (newTokenData.access) {
                handleNewRequest();
              }
            })
            .catch((refreshError) => {
              console.error("Token refresh failed, user may need to log in again.", refreshError);
            });
        } else {
          console.error("Error fetching documents:", error);
        }
      } 
    }
  };

  function uploadFileFromUrl(fileUrl, uploadEndpoint) {
    fetch(fileUrl)
      .then(response => response.blob())
      .then(blob => {
          const file = new File([blob], "uploadedFile.pdf", { type: blob.type });
          const formData = new FormData();
          dispatch(uploadDocument({ token: pdfEditortoken?.access, file }));
          navigate("/esign/new-esign", { replace: true });
      })
      .catch(error => console.error("Error fetching file:", error));
  }

  useEffect(() => {
    if (selectedPreviewDoc) {
      handlePreview(selectedPreviewDoc);
    }
  }, [selectedPreviewDoc]);

  const handlePreview = async (selectedPreviewDoc) => {
    logEvent('Clicked', 'Preview Clicked', 'Documents/Esigned');
    if(selectedPreviewDoc?.document_id)
    {
      setLoader(true)
      if(selectedPreviewDoc?.is_uploaded_to_s3)
      {
        console.log(selectedPreviewDoc)
        if (!selectedPreviewDoc?.uuid || !profileData?.username || !selectedPreviewDoc?.mobile) {
          setLoader(false);
          console.error("Missing required parameters for download.");
          return;
        }
        const payload = {
          "file_name": `PDFs/${profileData?.username}/${selectedPreviewDoc?.uuid}/${selectedPreviewDoc?.mobile}/pdfForEsign_signedFinal.pdf`
        };
        try {
          const response = await fetch(`${apiEndpoint.esignBaseUri}/EsignServices/download_pdf_file_from_s3`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
          });
  
          if (!response.ok) {
            setLoader(false);
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
      
          const data = await response.json();
          console.log(data)
          const documentUrl = getEsignDocumentUrl(data.url);

            if (!documentUrl) {
              setLoader(false);
              console.error("Document URL not found in response.");
              return;
            }
            const checkResponse = await fetch(documentUrl, { method: 'GET' });
            if (!checkResponse.ok) {
              setLoader(false);
              console.error(`Document not found: ${documentUrl}`);
              return;
            }
            var previewDocumnetData = {document_url: documentUrl, document_name: selectedPreviewDoc?.document_name}
            setPreviewDocument(true)
            setPreviewDoc(previewDocumnetData)
            setLoader(false);
        }
        catch (error) {
          console.error('Error occurred while fetching the URL:', error.message);
          setLoader(false);
        }
      }
      else
      {
        if(selectedPreviewDoc?.pdf_path)
        {
          const documentUrl = getSignedFinalPdfUrl(selectedPreviewDoc, profileData?.username);
          if (!documentUrl) {
            setLoader(false);
            console.error("Document URL not found in response.");
            return;
          }
          const checkResponse = await fetch(documentUrl, { method: 'GET' });
          if (!checkResponse.ok) {
            setLoader(false);
            console.error(`Document not found: ${documentUrl}`);
            return;
          }
          var previewDocumnetData = {document_url: documentUrl, document_name: selectedPreviewDoc?.document_name};
          setPreviewDocument(true);
          setPreviewDoc(previewDocumnetData);
          setLoader(false);
        }
        else
        {
          setLoader(false);
        }
      }
    }
  };

  const handleDownload = async (doc) => {
    logEvent('Clicked', 'Download Clicked', 'Documents/Esigned');
    if(doc?.is_uploaded_to_s3)
    {
      if (!doc?.uuid || !profileData?.username || !doc?.mobile) {
        console.error("Missing required parameters for download.");
        return;
      }
      setLoader(true);
      const payload = {
        "file_name": `PDFs/${profileData?.username}/${doc?.uuid}/${doc?.mobile}/pdfForEsign_signedFinal.pdf`
      };
      try {
        const response = await fetch(`${apiEndpoint.esignBaseUri}/EsignServices/download_pdf_file_from_s3`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          setLoader(false);
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
    
        const data = await response.json();
        console.log(data)
        const URLdata = getEsignDocumentUrl(data.url);
        var responsefile = await fetch(URLdata);
        if (!responsefile.ok) {
          setLoader(false);
          throw new Error(`Failed to fetch file: ${responsefile.statusText}`);
        }

        const blob = await responsefile.blob();
        const a = document.createElement('a');
        const url = window.URL.createObjectURL(blob);
        a.href = url;
        var fileName = "pdfForEsign_signedFinal";
        const file_extension = "pdf"
        a.download = fileName + '.' + file_extension;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        setLoader(false);

      } catch (error) {
        console.error('Error occurred while fetching the URL:', error.message);
        setLoader(false);
      }
    }
    else
    {
      if(doc?.pdf_path)
      { 
        setLoader(true);
        let URLdata = getSignedFinalPdfUrl(doc, profileData?.username);
        var responsefile = await fetch(URLdata);
        if (!responsefile.ok) {
          setLoader(false);
          throw new Error(`Failed to fetch file: ${responsefile.statusText}`);
        }

        const blob = await responsefile.blob();
        const a = document.createElement('a');
        const url = window.URL.createObjectURL(blob);
        a.href = url;
        var fileName = "pdfForEsign_signedFinal";
        const file_extension = "pdf"
        a.download = fileName + '.' + file_extension;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        setLoader(false);
      }
    }
  };

  const fetchSearchResults = async (searchText) => {
    logEvent('Clicked', 'Search Text', 'Documents/Esigned');
    if (searchText.length > 3) {
      setLoader(true);
      try {
        const response = await esignToolApi.getAllEsignedSearchDocument(searchText);
        setLoader(false);
        if (response?.status) {
          console.log(response)
          setDocuments(response.data.data);
          setTotalPages(response.data.pages);
        }
      } catch (error) {
        setLoader(false);
        console.error("Error searching data:", error);
      }
    }
  };
  
  const debouncedSearch = debounce((value) => {
    if(value) {
      fetchAllDocuments(currentPage, rowsPerPage, value);
      setCurrentPage(1);
    }
    else
    {
      // setSearchText("");
      fetchAllDocuments(currentPage, rowsPerPage, "");
      setCurrentPage(1);
    }
  }, 500);
  
  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchText(value);
    debouncedSearch(value);
  };

  const handleDocumentDetails = async (row) =>
  {
    logEvent('Clicked', 'Document details', 'Documents/Esigned');
    setLoader(true)
    try {
      const response = await esignToolApi.getAllSignerStatus(row?.document_id, row?.request_id);
      setLoader(false)
      console.log(response)
      if (response) {
        setOpenDetails(true)
        setSelectedDetailDoc(row)
        setSignersStatus(response?.data)
      }
    } catch (error) {
      setLoader(false)
      console.error("Error fetching data:", error);
    }
  }

  // const filteredDocs = documents.filter(
  //   (doc) =>
  //     doc.document_name.toLowerCase().includes(searchText.toLowerCase()) || doc.created_at.toLowerCase().includes(searchText.toLowerCase())
  // );

  return (
    <>
    {!openDetails && (<>
      <Box sx={{ display: "flex", ml: "30px" }}>
        <TextField
          placeholder="Search..."
          type="text"
          variant="outlined"
          onChange={handleSearchChange}
          sx={{
          boxSizing: "border-box",
          borderRadius: "10px",
          backgroundColor: "#fff",
          borderColor: "#E8E8E9",
          borderWidth: "5px",
          "& .MuiOutlinedInput-root": {
              borderColor: "#E8E8E9",
              backgroundColor: "none",
              "&:hover fieldset": {
              borderColor: "#E8E8E9",
              },
              "&.Mui-focused fieldset": {
              borderColor: "#E8E8E9",
              },
          },
          "& .MuiFormHelperText-root": {
              color: "#FF6666",
          },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {esignIcons.searchIcon}
              </InputAdornment>
            ),
            sx: { 
              borderRadius: "10px",
              width: 410,
              height: 40,
              fontSize: "16px",
              fontWeight: "500",
              "& input::-ms-reveal, & input::-ms-clear": {
                  display: "none",
              },
              "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                  display: "none",
                  WebkitAppearance: "none",
                  margin: 0,
              },
              "& input[type=number]": {
                  MozAppearance: "textfield",
              },
            },
          }}
        />
      </Box>
      <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: "none", height: "90%", mt: "15px" }}>
        <TableContainer sx={{ maxHeight: "88%" }}>
          <Table stickyHeader>
            <TableHead sx={{ background: "#F5F5F5", fontSize: "15px", fontWeight: 500, color: "#1D1D1D" }}>
              <TableRow>
                <TableCell sx={{  pl: "30px", }}>S. No.</TableCell>
                <TableCell>Document Name</TableCell>
                <TableCell>Created On</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tableloader ? (
                [...Array(rowsPerPage)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell sx={{ pl: "30px" }}>
                      <Skeleton variant="text" width={30} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={180} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={120} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={80} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="circular" width={30} height={30} />
                    </TableCell>
                  </TableRow>
                ))
              ) : documents.length > 0 ? (
                documents.map((row, index) => (
                    <TableRow key={row.uuid} onClick={() => handleDocumentDetails(row)} sx={{
                      "&:hover": { backgroundColor: "#fafafa" },
                    }}  >
                      <TableCell sx={{ pl: "30px", cursor: "pointer" }}>{((currentPage - 1) * rowsPerPage + index + 1).toString().padStart(2, "0")}</TableCell>
                      <TableCell sx={{ cursor: "pointer" }}>{row.document_name}</TableCell>
                      <TableCell sx={{ cursor: "pointer" }}>
                        {new Date(row.created_at).toLocaleString()}
                      </TableCell>
                      <TableCell sx={{ color: getStatusStyles(row.status).color, cursor: "pointer" }}>{row.status}</TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <IconButton
                          onClick={() => {
                            setSelectedPreviewDoc(null);
                            setTimeout(() => setSelectedPreviewDoc(row), 0);
                          }}
                        >
                          {esignIcons.eyeIcon}
                        </IconButton>
                        <IconButton
                          onClick={() => handleDownload(row)}
                        >
                          {esignIcons.downloadIcon}
                        </IconButton>
                        {/* <IconButton onClick={(e) => handleMenuOpen(e, row)}>
                        {esignIcons.moreIcon}
                        </IconButton> */}
                      </TableCell>
                    </TableRow>
                  ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No documents found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 20, 30, 50]}
          component="div"
          count={totalEntries}
          rowsPerPage={rowsPerPage}
          page={currentPage - 1}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            position: "sticky",
            bottom: 0,
            backgroundColor: "white",
            zIndex: 2,
          }}
        />
      </Paper>
      </>)}
      {openDetails && <DocumentDetails doc={selectedDetailDoc} setOpenDetails={setOpenDetails} openDetails={openDetails} signersStatus={signersStatus} handleDocumentDetails={handleDocumentDetails} />}
      {loader && <Loader />}
      {previewDocument && <PdfPreview previewDoc={previewDoc} setPreviewDoc={setPreviewDoc} previewDocument={previewDocument} setPreviewDocument={setPreviewDocument} />}
      </>
  );
};

export default EsignedDocument;
